/**
 * ТЗ 0083 — состояние ленты «Переписки»: письма и SMS грузятся разными
 * запросами, и у каждого своя загрузка и своя ошибка.
 *
 * ТЗ 0084: лента общая для договора и лида (`_detailShared/Correspondence`),
 * поэтому модель переехала из `Esign` сюда; `Esign/correspondenceModel`
 * остался переадресацией для старых импортов.
 *
 * Перепроверка UI: лента брала загрузку и ошибку только у писем — фильтр
 * «SMS» при сбое писем показывал «не удалось загрузить», пока SMS ещё
 * ехали — «SMS не уходили», а на «Всё» ошибка писем пряталась совсем.
 */
export type CorrespondenceFilter = 'all' | 'mail' | 'sms';

export interface ICorrespondenceSource {
  loading: boolean;
  failed: boolean;
  count: number;
}

export interface ICorrespondenceFeedState {
  /** Идёт первая загрузка того, что показывает фильтр. */
  loading: boolean;
  /** Письма не загрузились — строка об этом видна при любом их наличии. */
  mailFailed: boolean;
  smsFailed: boolean;
  /** Ключ «пусто» или null: пусто только то, что точно загружено. */
  emptyKey: 'mail' | 'sms' | 'all' | null;
}

export const correspondenceFeedState = (
  filter: CorrespondenceFilter,
  mail: ICorrespondenceSource,
  sms: ICorrespondenceSource | null,
): ICorrespondenceFeedState => {
  const showMail = filter !== 'sms';
  const showSms = filter !== 'mail' && sms !== null;
  const mailLoading = showMail && mail.loading && mail.count === 0;
  const smsLoading = showSms && Boolean(sms?.loading) && sms?.count === 0;
  const mailFailed = showMail && mail.failed;
  const smsFailed = showSms && Boolean(sms?.failed);
  const loading = mailLoading || smsLoading;
  const shown = (showMail ? mail.count : 0) + (showSms ? sms?.count ?? 0 : 0);
  let emptyKey: ICorrespondenceFeedState['emptyKey'] = null;
  if (shown === 0 && !loading && !mailFailed && !smsFailed) {
    if (filter === 'sms') emptyKey = 'sms';
    else if (filter === 'mail') emptyKey = 'mail';
    else emptyKey = 'all';
  }
  return { loading, mailFailed, smsFailed, emptyKey };
};

/** Счётчик у фильтра: число или «…», пока источник впервые грузится. */
export const correspondenceChipCount = (
  sources: Array<ICorrespondenceSource | null>,
): string => {
  const present = sources.filter(Boolean) as ICorrespondenceSource[];
  if (present.some((source) => source.loading && source.count === 0)) {
    return '…';
  }
  return String(present.reduce((sum, source) => sum + source.count, 0));
};

export interface ICorrespondenceRow<T> {
  kind: 'mail' | 'sms';
  /** Время строки в мс — для сортировки (новые сверху). */
  at: number;
  item: T;
}

/**
 * Одна лента из тредов писем и SMS по фильтру, новые сверху. Время — то,
 * что вернули `mailAt` / `smsAt` (ISO-строка); пустое — в конец ленты.
 */
export const mergeCorrespondence = <M, S>(
  filter: CorrespondenceFilter,
  mail: ReadonlyArray<M>,
  mailAt: (item: M) => string | null | undefined,
  sms: ReadonlyArray<S>,
  smsAt: (item: S) => string | null | undefined,
): Array<ICorrespondenceRow<M> | ICorrespondenceRow<S>> => {
  const time = (value: string | null | undefined) => {
    const parsed = value ? Date.parse(value) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const rows: Array<ICorrespondenceRow<M> | ICorrespondenceRow<S>> = [];
  if (filter !== 'sms') {
    mail.forEach((item) =>
      rows.push({ kind: 'mail', at: time(mailAt(item)), item }),
    );
  }
  if (filter !== 'mail') {
    sms.forEach((item) =>
      rows.push({ kind: 'sms', at: time(smsAt(item)), item }),
    );
  }
  return rows.sort((a, b) => b.at - a.at);
};
